import {
    CheckCircledIcon,
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronUpIcon,
    CopyIcon,
    EnvelopeClosedIcon,
    FileTextIcon,
    Pencil1Icon,
    PlusIcon,
    RocketIcon,
    StarIcon,
    TrashIcon,
} from '@radix-ui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LuChevronRight, LuSparkles } from 'react-icons/lu';
import { Link, useBeforeUnload, useBlocker, useParams } from 'react-router-dom';

import {
    CmsCtaTarget,
    CmsDraftPageContent,
    CmsImproveContentResponse,
    CmsInfoCardGridLayout,
    CmsInfoCardGridTone,
    CmsLeadFormLayout,
    CmsMediaTextSectionLayout,
    CmsPageModuleContent,
    CmsPageModuleType,
    CmsQuoteSectionLayout,
} from '@ndvr/cms-api';
import { DEFAULT_HERO_EYEBROW } from '@ndvr/cms-renderer-modules';

import { ConfirmModal } from '@/components/ConfirmModal';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/utils/cn';
import { copyText } from '@/utils/dom-utils';
import { useAiChatSidebarState } from '@/views/ai-chat/ai-chat-state';
import { marketingCmsSiteHistoryPath } from '@/views/marketing/marketing-routes';
import { Toast } from 'widgets';

import {
    canRemoveCmsModule,
    getAddableCmsModuleTypes,
    getCmsModuleDisplayTitle,
    getCmsModuleInstanceKey,
} from './cms-editor-state';
import {
    buildCmsFieldDescriptorLookup,
    CMS_FIELD_PATHS,
    CmsFieldDescriptorLookup,
    cmsFieldMaxLength,
} from './cms-field-descriptors';
import {
    DEFAULT_LEAD_FORM_BODY,
    DEFAULT_LEAD_FORM_HEADING,
    DEFAULT_LEAD_FORM_SUBMIT_LABEL,
} from './cms-lead-form-defaults';
import { useCmsPermissions } from './cms-permissions';
import {
    useCmsHistory,
    useCmsHomepageContent,
    useCmsHomepageModuleDescriptors,
    useCmsMicrosite,
    useImproveCmsHomepageModuleContent,
    useSubmitCmsForReview,
    useUpdateCmsHomepageContent,
} from './cms-queries';
import { CmsModuleListPreview } from './cms-rendering';
import { CmsStatusBadge } from './cms-ui';
import {
    cmsHttpsUrlValidationMessage,
    type CmsValidationIssue,
    cmsValidationIssuesByModuleIndex,
    draftContentForSave,
    normalizeHttpsUrl,
    validateCmsDraftForSave,
    validateCmsDraftForSubmit,
} from './cms-validation';
import { CmsImageAssetInput } from './CmsImageAssetInput';
import { CmsSectionPicker } from './CmsSectionPicker';

interface ModuleMeta {
    title: string;
    subtitle: string;
    icon: React.ComponentType<{ className?: string }>;
}

const MODULE_META: Partial<Record<CmsPageModuleType, ModuleMeta>> = {
    [CmsPageModuleType.HERO]: { title: 'Hero', subtitle: 'Headline + intro', icon: StarIcon },
    [CmsPageModuleType.ADVISOR_PROFILE]: { title: 'Advisor profile', subtitle: 'Bio + contact', icon: Pencil1Icon },
    [CmsPageModuleType.CONTACT_SECTION]: { title: 'Contact', subtitle: 'Ways to connect', icon: EnvelopeClosedIcon },
    [CmsPageModuleType.LEAD_FORM]: {
        title: 'Lead form',
        subtitle: 'Capture visitor interest',
        icon: EnvelopeClosedIcon,
    },
    [CmsPageModuleType.INFO_CARD_GRID]: { title: 'Info card grid', subtitle: 'Reusable cards', icon: StarIcon },
    [CmsPageModuleType.MEDIA_TEXT_SECTION]: { title: 'Media + text', subtitle: 'Image + copy', icon: FileTextIcon },
    [CmsPageModuleType.PROCESS_STEPS]: { title: 'Process steps', subtitle: 'Numbered flow', icon: CheckCircledIcon },
    [CmsPageModuleType.QUOTE_SECTION]: { title: 'Quote', subtitle: 'Pull quote', icon: Pencil1Icon },
    [CmsPageModuleType.RESOURCE_LIST]: { title: 'Resource list', subtitle: 'Approved links', icon: FileTextIcon },
    [CmsPageModuleType.IMPORTANT_DISCLOSURES]: {
        title: 'Important disclosures',
        subtitle: 'Visible compliance copy',
        icon: FileTextIcon,
    },
    [CmsPageModuleType.FAQ]: { title: 'FAQ', subtitle: 'Questions + answers', icon: FileTextIcon },
    [CmsPageModuleType.CTA]: { title: 'Call to action', subtitle: 'Closing + button', icon: EnvelopeClosedIcon },
};

const defaultModule = (moduleType: CmsPageModuleType): CmsPageModuleContent => {
    if (moduleType === CmsPageModuleType.HERO) {
        return { moduleType, hero: { eyebrow: DEFAULT_HERO_EYEBROW, headline: '', body: '' } };
    }
    if (moduleType === CmsPageModuleType.CTA) {
        return { moduleType, cta: { label: '', target: CmsCtaTarget.LEAD_FORM } };
    }
    if (moduleType === CmsPageModuleType.ADVISOR_PROFILE) {
        return { moduleType, advisorProfile: { name: '', title: '', credentials: [], bio: '' } };
    }
    if (moduleType === CmsPageModuleType.CONTACT_SECTION) {
        return { moduleType, contactSection: { heading: '', body: '', addressLines: [] } };
    }
    if (moduleType === CmsPageModuleType.LEAD_FORM) {
        return {
            moduleType,
            leadForm: {
                destinationEmail: '',
                eyebrow: '',
                heading: DEFAULT_LEAD_FORM_HEADING,
                body: DEFAULT_LEAD_FORM_BODY,
                layout: CmsLeadFormLayout.SPLIT,
                submitLabel: DEFAULT_LEAD_FORM_SUBMIT_LABEL,
                successMessage: '',
            },
        };
    }
    if (moduleType === CmsPageModuleType.INFO_CARD_GRID) {
        return {
            moduleType,
            infoCardGrid: {
                eyebrow: '',
                heading: '',
                body: '',
                layout: CmsInfoCardGridLayout.GRID,
                tone: CmsInfoCardGridTone.LIGHT,
                items: [{ title: '', body: '' }],
            },
        };
    }
    if (moduleType === CmsPageModuleType.MEDIA_TEXT_SECTION) {
        return {
            moduleType,
            mediaTextSection: {
                eyebrow: '',
                heading: '',
                body: '',
                layout: CmsMediaTextSectionLayout.IMAGE_RIGHT,
            },
        };
    }
    if (moduleType === CmsPageModuleType.PROCESS_STEPS) {
        return {
            moduleType,
            processSteps: {
                eyebrow: '',
                heading: '',
                body: '',
                steps: [
                    { title: '', body: '' },
                    { title: '', body: '' },
                ],
            },
        };
    }
    if (moduleType === CmsPageModuleType.QUOTE_SECTION) {
        return {
            moduleType,
            quoteSection: {
                eyebrow: '',
                quote: '',
                attributionName: '',
                attributionTitle: '',
                layout: CmsQuoteSectionLayout.LEFT,
            },
        };
    }
    if (moduleType === CmsPageModuleType.RESOURCE_LIST) {
        return {
            moduleType,
            resourceList: {
                eyebrow: '',
                heading: '',
                body: '',
                items: [{ title: '', description: '', linkLabel: '', url: '' }],
            },
        };
    }
    if (moduleType === CmsPageModuleType.IMPORTANT_DISCLOSURES) {
        return {
            moduleType,
            importantDisclosures: {
                heading: 'Important Disclosures',
                paragraphs: [
                    {
                        body: "This microsite is intended to describe the advisor's background, services, and general approach to financial planning. It does not constitute investment advice, a solicitation, or an offer to buy or sell any security.",
                    },
                    { body: 'Registration with the SEC does not imply a certain level of skill or training.' },
                ],
                footnotes: [],
            },
        };
    }
    if (moduleType === CmsPageModuleType.FAQ) {
        return { moduleType, faq: { eyebrow: '', heading: '', items: [{ question: '', answer: '' }] } };
    }
    throw new Error('Unsupported CMS module type');
};

const InspectorGroup = ({ label, children }: React.PropsWithChildren<{ label: string }>) => (
    <div className="space-y-4">
        <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{label}</p>
        {children}
    </div>
);

const InspectorField = ({ label, htmlFor, children }: React.PropsWithChildren<{ label: string; htmlFor?: string }>) => (
    <div className="space-y-2">
        <Label htmlFor={htmlFor}>{label}</Label>
        {children}
    </div>
);

const WARNING_FIELD_CLASS = 'border-amber-400 bg-amber-50/40 focus-visible:ring-amber-400';
const ERROR_FIELD_CLASS = 'border-destructive bg-destructive/5 focus-visible:ring-destructive/40';

const warningFieldClass = (hasWarning: boolean) => (hasWarning ? WARNING_FIELD_CLASS : undefined);
const fieldStateClass = (hasError: boolean, hasWarning: boolean) =>
    hasError ? ERROR_FIELD_CLASS : warningFieldClass(hasWarning);

const FIELD_LABELS: Record<string, string> = {
    calendarUrl: 'Calendar URL',
    linkedInUrl: 'LinkedIn URL',
    phone: 'Phone',
    email: 'Email',
    location: 'Location',
    ctaLabel: 'CTA label',
};

const URL_FIELD_PLACEHOLDERS: Record<string, string> = {
    calendarUrl: 'https://calendly.com/...',
    linkedInUrl: 'https://www.linkedin.com/in/...',
};

const fieldLabel = (field: string): string => FIELD_LABELS[field] ?? field;
const isUrlField = (field: string): boolean => field.toLowerCase().endsWith('url');
const inputTypeForField = (field: string): React.ComponentProps<typeof Input>['type'] =>
    isUrlField(field) ? 'url' : undefined;
const placeholderForField = (field: string): string | undefined => URL_FIELD_PLACEHOLDERS[field];
const normalizeUrlInputValue = (value: string): string => normalizeHttpsUrl(value) ?? value;

const INFO_CARD_GRID_LAYOUT_OPTIONS = [
    { value: CmsInfoCardGridLayout.GRID, label: 'Grid', wire: 'grid' },
    { value: CmsInfoCardGridLayout.COLUMNS, label: 'Columns', wire: 'columns' },
    { value: CmsInfoCardGridLayout.RULE_LIST, label: 'Rule list', wire: 'rule-list' },
    { value: CmsInfoCardGridLayout.LEFT, label: 'Left', wire: 'left' },
    { value: CmsInfoCardGridLayout.CENTERED, label: 'Centered', wire: 'centered' },
] as const;

const INFO_CARD_GRID_TONE_OPTIONS = [
    { value: CmsInfoCardGridTone.LIGHT, label: 'Light', swatch: 'border-stone-300 bg-white' },
    { value: CmsInfoCardGridTone.COCOA, label: 'Cocoa', swatch: 'border-cocoa-900 bg-cocoa-900' },
    { value: CmsInfoCardGridTone.SEAFOAM, label: 'Seafoam', swatch: 'border-seafoam-300 bg-seafoam-200' },
] as const;

const LEAD_FORM_LAYOUT_OPTIONS = [
    { value: CmsLeadFormLayout.SPLIT, label: 'Split', wire: 'split' },
    { value: CmsLeadFormLayout.CENTERED, label: 'Centered', wire: 'centered' },
] as const;

const QUOTE_SECTION_LAYOUT_OPTIONS = [
    { value: CmsQuoteSectionLayout.LEFT, label: 'Left', wire: 'left' },
    { value: CmsQuoteSectionLayout.CENTERED, label: 'Centered', wire: 'centered' },
] as const;

type InfoCardGridLayoutWire = (typeof INFO_CARD_GRID_LAYOUT_OPTIONS)[number]['wire'];
type LeadFormLayoutWire = (typeof LEAD_FORM_LAYOUT_OPTIONS)[number]['wire'];
type QuoteSectionLayoutWire = (typeof QUOTE_SECTION_LAYOUT_OPTIONS)[number]['wire'];

const InfoCardGridLayoutWireframe = ({ wire }: { wire: InfoCardGridLayoutWire }) => {
    if (wire === 'left') {
        return (
            <span className="grid h-9 grid-cols-[0.42fr_0.58fr] gap-1 rounded border border-stone-300 p-1">
                <span className="flex flex-col justify-center gap-1">
                    <span className="bg-cocoa-400 h-1.5 w-10 rounded-sm" />
                    <span className="h-1 w-full rounded-sm bg-stone-300" />
                    <span className="h-1 w-4/5 rounded-sm bg-stone-200" />
                </span>
                <span className="grid grid-cols-2 gap-px overflow-hidden rounded bg-stone-200">
                    {[0, 1, 2, 3].map((index) => (
                        <span key={index} className="bg-white p-0.5">
                            <span className="bg-cocoa-400 mb-0.5 block h-1 rounded-sm" />
                            <span className="block h-0.5 rounded-sm bg-stone-300" />
                        </span>
                    ))}
                </span>
            </span>
        );
    }

    if (wire === 'centered') {
        return (
            <span className="flex h-9 flex-col items-center justify-center gap-1 rounded border border-stone-300 px-1.5">
                <span className="bg-cocoa-400 h-1.5 w-12 rounded-sm" />
                <span className="h-1 w-16 rounded-sm bg-stone-300" />
                <span className="mt-1 grid w-full grid-cols-3 gap-px">
                    {[0, 1, 2].map((index) => (
                        <span key={index} className="h-2.5 rounded-sm border border-stone-200 bg-white" />
                    ))}
                </span>
            </span>
        );
    }

    if (wire === 'columns') {
        return (
            <span className="grid h-9 grid-cols-3 gap-1">
                {[0, 1, 2].map((index) => (
                    <span key={index} className="flex flex-col gap-1 border-l border-stone-300 pl-1">
                        <span className="bg-cocoa-400 h-1.5 rounded-sm" />
                        <span className="h-1 rounded-sm bg-stone-300" />
                        <span className="h-1 rounded-sm bg-stone-200" />
                    </span>
                ))}
            </span>
        );
    }

    if (wire === 'rule-list') {
        return (
            <span className="flex h-9 flex-col border-t border-stone-300">
                {[0, 1, 2].map((index) => (
                    <span
                        key={index}
                        className="grid flex-1 grid-cols-[0.4fr_0.6fr] items-center gap-2 border-b border-stone-200"
                    >
                        <span className="bg-cocoa-400 h-1.5 rounded-sm" />
                        <span className="h-1 rounded-sm bg-stone-300" />
                    </span>
                ))}
            </span>
        );
    }

    return (
        <span className="grid h-9 grid-cols-3 gap-px overflow-hidden rounded border border-stone-300 bg-stone-200">
            {[0, 1, 2, 3, 4, 5].map((index) => (
                <span key={index} className="bg-white p-1">
                    <span className="bg-cocoa-400 mb-1 block h-1.5 rounded-sm" />
                    <span className="block h-1 rounded-sm bg-stone-300" />
                </span>
            ))}
        </span>
    );
};

const InfoCardGridDesignControls = ({
    layout,
    tone,
    disabled,
    onLayoutChange,
    onToneChange,
}: {
    layout?: CmsInfoCardGridLayout;
    tone?: CmsInfoCardGridTone;
    disabled: boolean;
    onLayoutChange: (layout: CmsInfoCardGridLayout) => void;
    onToneChange: (tone: CmsInfoCardGridTone) => void;
}) => {
    const activeLayout = layout ?? CmsInfoCardGridLayout.GRID;
    const activeTone = tone ?? CmsInfoCardGridTone.LIGHT;

    return (
        <InspectorGroup label="Design">
            <div className="space-y-2">
                <Label>Layout</Label>
                <div className="grid grid-cols-2 gap-2">
                    {INFO_CARD_GRID_LAYOUT_OPTIONS.map((option) => {
                        const selected = option.value === activeLayout;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                disabled={disabled}
                                aria-pressed={selected}
                                onClick={() => onLayoutChange(option.value)}
                                className={cn(
                                    'rounded-md border bg-white p-2 text-left text-xs font-medium transition-colors',
                                    selected
                                        ? 'border-cocoa-900 text-cocoa-900 ring-cocoa-900 ring-1 ring-inset'
                                        : 'text-muted-foreground border-stone-200 hover:border-stone-400',
                                    disabled && 'cursor-not-allowed opacity-60',
                                )}
                            >
                                <InfoCardGridLayoutWireframe wire={option.wire} />
                                <span className="mt-2 block">{option.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
            <div className="space-y-2">
                <Label>Tone</Label>
                <div className="grid grid-cols-3 gap-2">
                    {INFO_CARD_GRID_TONE_OPTIONS.map((option) => {
                        const selected = option.value === activeTone;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                disabled={disabled}
                                aria-pressed={selected}
                                onClick={() => onToneChange(option.value)}
                                className={cn(
                                    'flex items-center gap-2 rounded-md border bg-white px-2.5 py-2 text-xs font-medium transition-colors',
                                    selected
                                        ? 'border-cocoa-900 text-cocoa-900 ring-cocoa-900 ring-1 ring-inset'
                                        : 'text-muted-foreground border-stone-200 hover:border-stone-400',
                                    disabled && 'cursor-not-allowed opacity-60',
                                )}
                            >
                                <span className={cn('h-3.5 w-3.5 rounded-full border', option.swatch)} />
                                {option.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        </InspectorGroup>
    );
};

const LeadFormLayoutWireframe = ({ wire }: { wire: LeadFormLayoutWire }) => {
    if (wire === 'centered') {
        return (
            <span className="flex h-9 flex-col items-center justify-center gap-1 rounded border border-stone-300 bg-white px-2">
                <span className="bg-cocoa-400 h-1.5 w-16 rounded-sm" />
                <span className="h-1 w-20 rounded-sm bg-stone-300" />
                <span className="mt-1 h-3 w-24 rounded border border-stone-300 bg-stone-100" />
            </span>
        );
    }

    return (
        <span className="grid h-9 grid-cols-[0.45fr_0.55fr] gap-1 rounded border border-stone-300 bg-white p-2">
            <span className="flex flex-col justify-center gap-1">
                <span className="bg-cocoa-400 h-1.5 w-10 rounded-sm" />
                <span className="h-1 w-12 rounded-sm bg-stone-300" />
            </span>
            <span className="rounded border border-stone-300 bg-stone-100" />
        </span>
    );
};

const LeadFormDesignControls = ({
    layout,
    disabled,
    onLayoutChange,
}: {
    layout?: CmsLeadFormLayout;
    disabled: boolean;
    onLayoutChange: (layout: CmsLeadFormLayout) => void;
}) => {
    const activeLayout = layout ?? CmsLeadFormLayout.SPLIT;

    return (
        <InspectorGroup label="Design">
            <div className="space-y-2">
                <Label>Layout</Label>
                <div className="grid grid-cols-2 gap-2">
                    {LEAD_FORM_LAYOUT_OPTIONS.map((option) => {
                        const selected = option.value === activeLayout;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                disabled={disabled}
                                aria-pressed={selected}
                                onClick={() => onLayoutChange(option.value)}
                                className={cn(
                                    'rounded-md border bg-white p-2 text-left text-xs font-medium transition-colors',
                                    selected
                                        ? 'border-cocoa-900 text-cocoa-900 ring-cocoa-900 ring-1 ring-inset'
                                        : 'text-muted-foreground border-stone-200 hover:border-stone-400',
                                    disabled && 'cursor-not-allowed opacity-60',
                                )}
                            >
                                <LeadFormLayoutWireframe wire={option.wire} />
                                <span className="mt-2 block">{option.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </InspectorGroup>
    );
};

const QuoteSectionLayoutWireframe = ({ wire }: { wire: QuoteSectionLayoutWire }) => {
    if (wire === 'centered') {
        return (
            <span className="bg-cocoa-900 flex h-9 flex-col items-center justify-center gap-1 rounded border border-stone-300 px-2">
                <span className="bg-seafoam-200/70 h-1 w-14 rounded-sm" />
                <span className="h-1.5 w-20 rounded-sm bg-white" />
                <span className="h-1 w-16 rounded-sm bg-white/70" />
            </span>
        );
    }

    return (
        <span className="bg-cocoa-900 grid h-9 grid-cols-[0.78fr_0.22fr] gap-1 rounded border border-stone-300 p-2">
            <span className="flex flex-col justify-center gap-1">
                <span className="bg-seafoam-200/70 h-1 w-12 rounded-sm" />
                <span className="h-1.5 w-full rounded-sm bg-white" />
                <span className="h-1 w-3/4 rounded-sm bg-white/70" />
            </span>
            <span />
        </span>
    );
};

const QuoteSectionDesignControls = ({
    layout,
    disabled,
    onLayoutChange,
}: {
    layout?: CmsQuoteSectionLayout;
    disabled: boolean;
    onLayoutChange: (layout: CmsQuoteSectionLayout) => void;
}) => {
    const activeLayout = layout ?? CmsQuoteSectionLayout.LEFT;

    return (
        <InspectorGroup label="Design">
            <div className="space-y-2">
                <Label>Layout</Label>
                <div className="grid grid-cols-2 gap-2">
                    {QUOTE_SECTION_LAYOUT_OPTIONS.map((option) => {
                        const selected = option.value === activeLayout;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                disabled={disabled}
                                aria-pressed={selected}
                                onClick={() => onLayoutChange(option.value)}
                                className={cn(
                                    'rounded-md border bg-white p-2 text-left text-xs font-medium transition-colors',
                                    selected
                                        ? 'border-cocoa-900 text-cocoa-900 ring-cocoa-900 ring-1 ring-inset'
                                        : 'text-muted-foreground border-stone-200 hover:border-stone-400',
                                    disabled && 'cursor-not-allowed opacity-60',
                                )}
                            >
                                <QuoteSectionLayoutWireframe wire={option.wire} />
                                <span className="mt-2 block">{option.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </InspectorGroup>
    );
};

type CmsListEditorField<TItem> = {
    key: keyof TItem;
    label: string;
    multiline?: boolean;
    maxLength?: number;
};

interface CmsListEditorProps<TItem extends Record<string, string | undefined>> {
    label: string;
    addLabel: string;
    emptyText: string;
    items: TItem[];
    fields: CmsListEditorField<TItem>[];
    disabled: boolean;
    pathPrefix?: string;
    warningPaths?: Set<string>;
    createItem: () => TItem;
    onChange: (items: TItem[]) => void;
    normalizeItem?: (item: TItem) => TItem;
    renderItemAccessory?: (item: TItem, index: number) => React.ReactNode;
}

const CmsListEditor = <TItem extends Record<string, string | undefined>>({
    label,
    addLabel,
    emptyText,
    items,
    fields,
    disabled,
    pathPrefix,
    warningPaths,
    createItem,
    onChange,
    normalizeItem = (item) => item,
    renderItemAccessory,
}: CmsListEditorProps<TItem>) => {
    const updateItem = (index: number, field: keyof TItem, value: string) => {
        onChange(items.map((item, i) => (i === index ? normalizeItem({ ...item, [field]: value }) : item)));
    };

    const normalizeUrlItem = (index: number, field: keyof TItem, value: string) => {
        if (!isUrlField(String(field))) {
            return;
        }

        const normalizedValue = normalizeUrlInputValue(value);
        if (normalizedValue !== value) {
            updateItem(index, field, normalizedValue);
        }
    };

    const removeItem = (index: number) => {
        onChange(items.filter((_, i) => i !== index));
    };

    const moveItem = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= items.length) {
            return;
        }
        const next = [...items];
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
                <Label>{label}</Label>
                <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    disabled={disabled}
                    onClick={() => onChange([...items, createItem()])}
                >
                    <PlusIcon className="h-4 w-4" />
                    {addLabel}
                </Button>
            </div>
            {items.length === 0 ? (
                <p className="text-muted-foreground rounded-md border border-dashed px-3 py-4 text-sm">{emptyText}</p>
            ) : (
                <div className="space-y-3">
                    {items.map((item, index) => (
                        <div key={index} className="space-y-3 rounded-md border p-3">
                            <div className="flex items-center justify-between gap-3">
                                <p className="text-sm font-medium">
                                    {label} {index + 1}
                                </p>
                                <div className="flex items-center gap-1">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        disabled={disabled || index === 0}
                                        aria-label={`Move ${label.toLowerCase()} ${index + 1} up`}
                                        onClick={() => moveItem(index, -1)}
                                    >
                                        <ChevronUpIcon className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        disabled={disabled || index === items.length - 1}
                                        aria-label={`Move ${label.toLowerCase()} ${index + 1} down`}
                                        onClick={() => moveItem(index, 1)}
                                    >
                                        <ChevronDownIcon className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        disabled={disabled}
                                        aria-label={`Remove ${label.toLowerCase()} ${index + 1}`}
                                        onClick={() => removeItem(index)}
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            {fields.map((field) => {
                                const idPrefix = pathPrefix ? `${pathPrefix}-` : '';
                                const id = `${idPrefix}${String(field.key)}-${index}`;
                                const hasWarning =
                                    !!pathPrefix && !!warningPaths?.has(`${pathPrefix}.${index}.${String(field.key)}`);
                                const hasUrlError =
                                    isUrlField(String(field.key)) && !!cmsHttpsUrlValidationMessage(item[field.key]);
                                const fieldClassName = fieldStateClass(hasUrlError, hasWarning);
                                return (
                                    <InspectorField key={String(field.key)} label={field.label} htmlFor={id}>
                                        {field.multiline ? (
                                            <Textarea
                                                id={id}
                                                rows={3}
                                                value={item[field.key] ?? ''}
                                                disabled={disabled}
                                                maxLength={field.maxLength}
                                                aria-invalid={hasUrlError || undefined}
                                                className={fieldClassName}
                                                onChange={(event) => updateItem(index, field.key, event.target.value)}
                                            />
                                        ) : (
                                            <Input
                                                id={id}
                                                type={inputTypeForField(String(field.key))}
                                                value={item[field.key] ?? ''}
                                                disabled={disabled}
                                                maxLength={field.maxLength}
                                                aria-invalid={hasUrlError || undefined}
                                                className={fieldClassName}
                                                onChange={(event) => updateItem(index, field.key, event.target.value)}
                                                onBlur={(event) =>
                                                    normalizeUrlItem(index, field.key, event.target.value)
                                                }
                                            />
                                        )}
                                    </InspectorField>
                                );
                            })}
                            {renderItemAccessory?.(item, index)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const linesToList = (value: string): string[] =>
    value
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

const listToLines = (values?: string[]): string => values?.join('\n') ?? '';

type CmsGridListItem = { title?: string; body?: string; ctaLabel?: string; ctaUrl?: string };
type CmsCredentialListItem = { value?: string };
type CmsProcessStepListItem = { title?: string; body?: string };
type CmsResourceListEditorItem = { title?: string; description?: string; linkLabel?: string; url?: string };
type CmsImportantDisclosureParagraphListItem = { body?: string };
type CmsImportantDisclosureFootnoteListItem = { key?: string; label?: string; body?: string };
type CmsFaqListItem = { question?: string; answer?: string };

const gridItemFields = (
    fieldDescriptors: CmsFieldDescriptorLookup,
    paths: { title: string; body: string; ctaLabel: string },
): CmsListEditorField<CmsGridListItem>[] => [
    { key: 'title', label: 'Title', maxLength: cmsFieldMaxLength(fieldDescriptors, paths.title) },
    { key: 'body', label: 'Body', multiline: true, maxLength: cmsFieldMaxLength(fieldDescriptors, paths.body) },
    { key: 'ctaLabel', label: 'Button label', maxLength: cmsFieldMaxLength(fieldDescriptors, paths.ctaLabel) },
    { key: 'ctaUrl', label: 'Button URL' },
];

const credentialFields = (fieldDescriptors: CmsFieldDescriptorLookup): CmsListEditorField<CmsCredentialListItem>[] => [
    {
        key: 'value',
        label: 'Credential',
        maxLength: cmsFieldMaxLength(fieldDescriptors, CMS_FIELD_PATHS.advisorProfileCredentials),
    },
];

const faqItemFields = (fieldDescriptors: CmsFieldDescriptorLookup): CmsListEditorField<CmsFaqListItem>[] => [
    {
        key: 'question',
        label: 'Question',
        maxLength: cmsFieldMaxLength(fieldDescriptors, CMS_FIELD_PATHS.faqItemQuestion),
    },
    {
        key: 'answer',
        label: 'Answer',
        multiline: true,
        maxLength: cmsFieldMaxLength(fieldDescriptors, CMS_FIELD_PATHS.faqItemAnswer),
    },
];

const processStepFields = (
    fieldDescriptors: CmsFieldDescriptorLookup,
): CmsListEditorField<CmsProcessStepListItem>[] => [
    {
        key: 'title',
        label: 'Title',
        maxLength: cmsFieldMaxLength(fieldDescriptors, CMS_FIELD_PATHS.processStepsItemTitle),
    },
    {
        key: 'body',
        label: 'Body',
        multiline: true,
        maxLength: cmsFieldMaxLength(fieldDescriptors, CMS_FIELD_PATHS.processStepsItemBody),
    },
];

const resourceItemFields = (
    fieldDescriptors: CmsFieldDescriptorLookup,
): CmsListEditorField<CmsResourceListEditorItem>[] => [
    {
        key: 'title',
        label: 'Title',
        maxLength: cmsFieldMaxLength(fieldDescriptors, CMS_FIELD_PATHS.resourceListItemTitle),
    },
    {
        key: 'description',
        label: 'Description',
        multiline: true,
        maxLength: cmsFieldMaxLength(fieldDescriptors, CMS_FIELD_PATHS.resourceListItemDescription),
    },
    {
        key: 'linkLabel',
        label: 'Link label',
        maxLength: cmsFieldMaxLength(fieldDescriptors, CMS_FIELD_PATHS.resourceListItemLinkLabel),
    },
    { key: 'url', label: 'HTTPS URL' },
];

const importantDisclosureParagraphFields = (
    fieldDescriptors: CmsFieldDescriptorLookup,
): CmsListEditorField<CmsImportantDisclosureParagraphListItem>[] => [
    {
        key: 'body',
        label: 'Paragraph',
        multiline: true,
        maxLength: cmsFieldMaxLength(fieldDescriptors, CMS_FIELD_PATHS.importantDisclosureParagraphBody),
    },
];

const importantDisclosureFootnoteFields = (
    fieldDescriptors: CmsFieldDescriptorLookup,
): CmsListEditorField<CmsImportantDisclosureFootnoteListItem>[] => [
    {
        key: 'key',
        label: 'Key (use [[key]])',
        maxLength: cmsFieldMaxLength(fieldDescriptors, CMS_FIELD_PATHS.importantDisclosureFootnoteKey),
    },
    {
        key: 'label',
        label: 'Label',
        maxLength: cmsFieldMaxLength(fieldDescriptors, CMS_FIELD_PATHS.importantDisclosureFootnoteLabel),
    },
    {
        key: 'body',
        label: 'Footnote',
        multiline: true,
        maxLength: cmsFieldMaxLength(fieldDescriptors, CMS_FIELD_PATHS.importantDisclosureFootnoteBody),
    },
];

const createGridItem = (): CmsGridListItem => ({ title: '', body: '' });

const createCredentialItem = (): CmsCredentialListItem => ({ value: '' });

const createProcessStep = (): CmsProcessStepListItem => ({ title: '', body: '' });

const createResourceItem = (): CmsResourceListEditorItem => ({ title: '', description: '', linkLabel: '', url: '' });

const createImportantDisclosureParagraph = (): CmsImportantDisclosureParagraphListItem => ({ body: '' });

const createImportantDisclosureFootnote = (): CmsImportantDisclosureFootnoteListItem => ({
    key: '',
    label: '',
    body: '',
});

const disclosureFootnoteToken = (key?: string): string => (key?.trim() ? `[[${key.trim()}]]` : '');

const disclosureFootnoteKeyFromLabel = (label?: string): string =>
    (label ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64)
        .replace(/-+$/g, '');

const normalizeImportantDisclosureFootnote = (
    item: CmsImportantDisclosureFootnoteListItem,
): CmsImportantDisclosureFootnoteListItem => {
    if (item.key?.trim() || !item.label?.trim()) {
        return item;
    }
    return { ...item, key: disclosureFootnoteKeyFromLabel(item.label) };
};

const DisclosureFootnoteTokenAccessory = ({ item }: { item: CmsImportantDisclosureFootnoteListItem }) => {
    const token = disclosureFootnoteToken(item.key);
    if (!token) {
        return null;
    }

    const copyToken = async () => {
        const didCopy = await copyText(token);
        if (!didCopy) {
            Toast.forError(new Error('Clipboard copy failed.'), 'Failed to copy disclosure footnote token.');
        }
    };

    return (
        <div className="flex items-center justify-between gap-2 rounded-md bg-stone-50 px-3 py-2 text-xs">
            <code className="text-cocoa-900">{token}</code>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={`Copy ${token}`}
                onClick={copyToken}
            >
                <CopyIcon className="h-4 w-4" />
            </Button>
        </div>
    );
};

const createFaqItem = (): CmsFaqListItem => ({ question: '', answer: '' });

const normalizeGridItem = (item: CmsGridListItem): CmsGridListItem => ({
    ...item,
    ctaLabel: item.ctaLabel || undefined,
    ctaUrl: item.ctaUrl || undefined,
});

const credentialItemsFromList = (credentials?: string[]): CmsCredentialListItem[] =>
    (credentials ?? []).map((credential) => ({ value: credential }));

const credentialListFromItems = (items: CmsCredentialListItem[]): string[] => items.map((item) => item.value ?? '');

const hasText = (value?: string): boolean => !!value?.trim();

const ctaTarget = (module: CmsPageModuleContent): CmsCtaTarget => {
    if (module.cta?.target) {
        return module.cta.target;
    }
    return hasText(module.cta?.url) ? CmsCtaTarget.URL : CmsCtaTarget.LEAD_FORM;
};

interface SectionInspectorProps {
    cmsMicrositeGUID: string;
    moduleKey: string;
    module: CmsPageModuleContent | undefined;
    modules: CmsPageModuleContent[];
    canEdit: boolean;
    fieldDescriptors: CmsFieldDescriptorLookup;
    issues: CmsValidationIssue[];
    onChange: (next: CmsPageModuleContent) => void;
}

const SectionInspector = ({
    cmsMicrositeGUID,
    moduleKey,
    module,
    modules,
    canEdit,
    fieldDescriptors,
    issues,
    onChange,
}: SectionInspectorProps) => {
    const maxLength = (path: string) => cmsFieldMaxLength(fieldDescriptors, path);
    const warningPaths = useMemo(
        () => new Set(issues.map((issue) => issue.path).filter(Boolean) as string[]),
        [issues],
    );
    const fieldWarning = (path: string) => warningFieldClass(warningPaths.has(path));
    const urlFieldClass = (path: string, value: string | undefined) =>
        fieldStateClass(!!cmsHttpsUrlValidationMessage(value), warningPaths.has(path));

    if (!module) {
        return (
            <p className="text-muted-foreground text-sm">Select a section on the left, or add one to start editing.</p>
        );
    }

    if (module.moduleType === CmsPageModuleType.HERO) {
        return (
            <>
                <InspectorGroup label="Content">
                    <InspectorField label="Eyebrow" htmlFor="heroEyebrow">
                        <Input
                            id="heroEyebrow"
                            value={module.hero?.eyebrow ?? DEFAULT_HERO_EYEBROW}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.heroEyebrow)}
                            className={fieldWarning('hero.eyebrow')}
                            onChange={(event) =>
                                onChange({ ...module, hero: { ...module.hero!, eyebrow: event.target.value } })
                            }
                        />
                    </InspectorField>
                    <InspectorField label="Headline" htmlFor="heroHeadline">
                        <Input
                            id="heroHeadline"
                            value={module.hero?.headline ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.heroHeadline)}
                            className={fieldWarning('hero.headline')}
                            onChange={(event) =>
                                onChange({ ...module, hero: { ...module.hero!, headline: event.target.value } })
                            }
                        />
                    </InspectorField>
                    <InspectorField label="Subheading" htmlFor="heroBody">
                        <Textarea
                            id="heroBody"
                            rows={4}
                            value={module.hero?.body ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.heroBody)}
                            onChange={(event) =>
                                onChange({ ...module, hero: { ...module.hero!, body: event.target.value } })
                            }
                        />
                    </InspectorField>
                </InspectorGroup>
                <InspectorGroup label="Media">
                    <CmsImageAssetInput
                        key={moduleKey}
                        cmsMicrositeGUID={cmsMicrositeGUID}
                        assetGuid={module.hero?.imageAssetId}
                        disabled={!canEdit}
                        label="Upload image"
                        onChange={(assetGuid) =>
                            onChange({ ...module, hero: { ...module.hero!, imageAssetId: assetGuid } })
                        }
                    />
                </InspectorGroup>
            </>
        );
    }

    if (module.moduleType === CmsPageModuleType.CTA) {
        const target = ctaTarget(module);
        const hasLeadForm = modules.some((draftModule) => draftModule.moduleType === CmsPageModuleType.LEAD_FORM);
        const showMissingLeadFormWarning = target === CmsCtaTarget.LEAD_FORM && !hasLeadForm;

        return (
            <InspectorGroup label="Content">
                <InspectorField label="Button label" htmlFor="ctaLabel">
                    <Input
                        id="ctaLabel"
                        value={module.cta?.label ?? ''}
                        disabled={!canEdit}
                        maxLength={maxLength(CMS_FIELD_PATHS.ctaLabel)}
                        className={fieldWarning('cta.label')}
                        onChange={(event) =>
                            onChange({ ...module, cta: { ...module.cta!, label: event.target.value } })
                        }
                    />
                </InspectorField>
                <InspectorField label="Button target" htmlFor="ctaTarget">
                    <Select
                        value={target}
                        disabled={!canEdit}
                        onValueChange={(value) => {
                            const nextTarget = value as CmsCtaTarget;
                            onChange({
                                ...module,
                                cta: {
                                    ...module.cta!,
                                    target: nextTarget,
                                    url: nextTarget === CmsCtaTarget.URL ? module.cta?.url : undefined,
                                },
                            });
                        }}
                    >
                        <SelectTrigger id="ctaTarget" className={cn('w-full', fieldWarning('cta.url'))}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={CmsCtaTarget.LEAD_FORM}>Lead form</SelectItem>
                            <SelectItem value={CmsCtaTarget.URL}>External URL</SelectItem>
                        </SelectContent>
                    </Select>
                    {showMissingLeadFormWarning && (
                        <p className="mt-1.5 text-xs leading-5 text-amber-700">
                            Add a lead form section so this button has somewhere to go.
                        </p>
                    )}
                </InspectorField>
                {target === CmsCtaTarget.URL && (
                    <InspectorField label="HTTPS URL" htmlFor="ctaUrl">
                        <Input
                            id="ctaUrl"
                            value={module.cta?.url ?? ''}
                            disabled={!canEdit}
                            aria-invalid={!!cmsHttpsUrlValidationMessage(module.cta?.url) || undefined}
                            className={urlFieldClass('cta.url', module.cta?.url)}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    cta: { ...module.cta!, target: CmsCtaTarget.URL, url: event.target.value },
                                })
                            }
                            onBlur={(event) =>
                                onChange({
                                    ...module,
                                    cta: {
                                        ...module.cta!,
                                        target: CmsCtaTarget.URL,
                                        url: normalizeUrlInputValue(event.target.value),
                                    },
                                })
                            }
                        />
                    </InspectorField>
                )}
            </InspectorGroup>
        );
    }

    if (module.moduleType === CmsPageModuleType.ADVISOR_PROFILE) {
        return (
            <>
                <InspectorGroup label="Profile">
                    <InspectorField label="Name" htmlFor="advisorName">
                        <Input
                            id="advisorName"
                            value={module.advisorProfile?.name ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.advisorProfileName)}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    advisorProfile: { ...module.advisorProfile!, name: event.target.value },
                                })
                            }
                        />
                    </InspectorField>
                    <InspectorField label="Title" htmlFor="advisorTitle">
                        <Input
                            id="advisorTitle"
                            value={module.advisorProfile?.title ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.advisorProfileTitle)}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    advisorProfile: { ...module.advisorProfile!, title: event.target.value },
                                })
                            }
                        />
                    </InspectorField>
                    <CmsListEditor<CmsCredentialListItem>
                        label="Credential"
                        addLabel="Add credential"
                        emptyText="No credentials yet."
                        items={credentialItemsFromList(module.advisorProfile?.credentials)}
                        fields={credentialFields(fieldDescriptors)}
                        disabled={!canEdit}
                        createItem={createCredentialItem}
                        onChange={(items) =>
                            onChange({
                                ...module,
                                advisorProfile: {
                                    ...module.advisorProfile!,
                                    credentials: credentialListFromItems(items),
                                },
                            })
                        }
                    />
                    <InspectorField label="Bio" htmlFor="advisorBio">
                        <Textarea
                            id="advisorBio"
                            rows={5}
                            value={module.advisorProfile?.bio ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.advisorProfileBio)}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    advisorProfile: { ...module.advisorProfile!, bio: event.target.value },
                                })
                            }
                        />
                    </InspectorField>
                </InspectorGroup>
                <InspectorGroup label="Contact">
                    {(['phone', 'email', 'calendarUrl', 'linkedInUrl', 'location'] as const).map((field) => {
                        const value = module.advisorProfile?.[field] ?? '';
                        const path = `advisorProfile.${field}`;
                        const fieldIsUrl = isUrlField(field);
                        return (
                            <InspectorField key={field} label={fieldLabel(field)} htmlFor={`advisor-${field}`}>
                                <Input
                                    id={`advisor-${field}`}
                                    type={inputTypeForField(field)}
                                    value={value}
                                    placeholder={placeholderForField(field)}
                                    disabled={!canEdit}
                                    maxLength={
                                        field === 'phone'
                                            ? maxLength(CMS_FIELD_PATHS.advisorProfilePhone)
                                            : field === 'email'
                                              ? maxLength(CMS_FIELD_PATHS.advisorProfileEmail)
                                              : field === 'location'
                                                ? maxLength(CMS_FIELD_PATHS.advisorProfileLocation)
                                                : undefined
                                    }
                                    aria-invalid={fieldIsUrl && cmsHttpsUrlValidationMessage(value) ? true : undefined}
                                    className={fieldIsUrl ? urlFieldClass(path, value) : fieldWarning(path)}
                                    onChange={(event) =>
                                        onChange({
                                            ...module,
                                            advisorProfile: { ...module.advisorProfile!, [field]: event.target.value },
                                        })
                                    }
                                    onBlur={(event) => {
                                        if (!fieldIsUrl) {
                                            return;
                                        }
                                        onChange({
                                            ...module,
                                            advisorProfile: {
                                                ...module.advisorProfile!,
                                                [field]: normalizeUrlInputValue(event.target.value),
                                            },
                                        });
                                    }}
                                />
                            </InspectorField>
                        );
                    })}
                </InspectorGroup>
                <InspectorGroup label="Media">
                    <CmsImageAssetInput
                        key={moduleKey}
                        cmsMicrositeGUID={cmsMicrositeGUID}
                        assetGuid={module.advisorProfile?.imageAssetId}
                        disabled={!canEdit}
                        label="Upload headshot"
                        onChange={(assetGuid) =>
                            onChange({
                                ...module,
                                advisorProfile: { ...module.advisorProfile!, imageAssetId: assetGuid },
                            })
                        }
                    />
                </InspectorGroup>
            </>
        );
    }

    if (module.moduleType === CmsPageModuleType.CONTACT_SECTION) {
        return (
            <InspectorGroup label="Contact">
                <InspectorField label="Heading" htmlFor="contactHeading">
                    <Input
                        id="contactHeading"
                        value={module.contactSection?.heading ?? ''}
                        disabled={!canEdit}
                        maxLength={maxLength(CMS_FIELD_PATHS.contactSectionHeading)}
                        onChange={(event) =>
                            onChange({
                                ...module,
                                contactSection: { ...module.contactSection!, heading: event.target.value },
                            })
                        }
                    />
                </InspectorField>
                <InspectorField label="Body" htmlFor="contactBody">
                    <Textarea
                        id="contactBody"
                        rows={4}
                        value={module.contactSection?.body ?? ''}
                        disabled={!canEdit}
                        maxLength={maxLength(CMS_FIELD_PATHS.contactSectionBody)}
                        onChange={(event) =>
                            onChange({
                                ...module,
                                contactSection: { ...module.contactSection!, body: event.target.value },
                            })
                        }
                    />
                </InspectorField>
                {(['phone', 'email', 'calendarUrl', 'ctaLabel'] as const).map((field) => {
                    const value = module.contactSection?.[field] ?? '';
                    const path = `contactSection.${field}`;
                    const fieldIsUrl = isUrlField(field);
                    return (
                        <InspectorField key={field} label={fieldLabel(field)} htmlFor={`contact-${field}`}>
                            <Input
                                id={`contact-${field}`}
                                type={inputTypeForField(field)}
                                value={value}
                                placeholder={placeholderForField(field)}
                                disabled={!canEdit}
                                maxLength={
                                    field === 'phone'
                                        ? maxLength(CMS_FIELD_PATHS.contactSectionPhone)
                                        : field === 'email'
                                          ? maxLength(CMS_FIELD_PATHS.contactSectionEmail)
                                          : field === 'ctaLabel'
                                            ? maxLength(CMS_FIELD_PATHS.contactSectionCtaLabel)
                                            : undefined
                                }
                                aria-invalid={fieldIsUrl && cmsHttpsUrlValidationMessage(value) ? true : undefined}
                                className={fieldIsUrl ? urlFieldClass(path, value) : fieldWarning(path)}
                                onChange={(event) =>
                                    onChange({
                                        ...module,
                                        contactSection: { ...module.contactSection!, [field]: event.target.value },
                                    })
                                }
                                onBlur={(event) => {
                                    if (!fieldIsUrl) {
                                        return;
                                    }
                                    onChange({
                                        ...module,
                                        contactSection: {
                                            ...module.contactSection!,
                                            [field]: normalizeUrlInputValue(event.target.value),
                                        },
                                    });
                                }}
                            />
                        </InspectorField>
                    );
                })}
                <InspectorField label="Address lines" htmlFor="contactAddressLines">
                    <Textarea
                        id="contactAddressLines"
                        rows={3}
                        value={listToLines(module.contactSection?.addressLines)}
                        disabled={!canEdit}
                        onChange={(event) =>
                            onChange({
                                ...module,
                                contactSection: {
                                    ...module.contactSection!,
                                    addressLines: linesToList(event.target.value),
                                },
                            })
                        }
                    />
                </InspectorField>
            </InspectorGroup>
        );
    }

    if (module.moduleType === CmsPageModuleType.LEAD_FORM) {
        const leadFormLayout = module.leadForm?.layout ?? CmsLeadFormLayout.SPLIT;
        return (
            <>
                <LeadFormDesignControls
                    layout={leadFormLayout}
                    disabled={!canEdit}
                    onLayoutChange={(layout) =>
                        onChange({
                            ...module,
                            leadForm: { ...module.leadForm!, layout },
                        })
                    }
                />
                <InspectorGroup label="Lead form">
                    <InspectorField label="Heading" htmlFor="leadFormHeading">
                        <Input
                            id="leadFormHeading"
                            value={module.leadForm?.heading ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.leadFormHeading)}
                            className={fieldWarning('leadForm.heading')}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    leadForm: { ...module.leadForm!, heading: event.target.value },
                                })
                            }
                        />
                    </InspectorField>
                    <InspectorField label="Subtext" htmlFor="leadFormBody">
                        <Textarea
                            id="leadFormBody"
                            rows={3}
                            value={module.leadForm?.body ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.leadFormBody)}
                            className={fieldWarning('leadForm.body')}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    leadForm: { ...module.leadForm!, body: event.target.value },
                                })
                            }
                        />
                    </InspectorField>
                    <InspectorField label="Destination email" htmlFor="leadFormDestinationEmail">
                        <Input
                            id="leadFormDestinationEmail"
                            type="email"
                            value={module.leadForm?.destinationEmail ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.leadFormDestinationEmail)}
                            className={fieldWarning('leadForm.destinationEmail')}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    leadForm: { ...module.leadForm!, destinationEmail: event.target.value },
                                })
                            }
                        />
                    </InspectorField>
                </InspectorGroup>
            </>
        );
    }

    if (module.moduleType === CmsPageModuleType.INFO_CARD_GRID) {
        return (
            <>
                <InfoCardGridDesignControls
                    layout={module.infoCardGrid?.layout}
                    tone={module.infoCardGrid?.tone}
                    disabled={!canEdit}
                    onLayoutChange={(layout) =>
                        onChange({ ...module, infoCardGrid: { ...module.infoCardGrid!, layout } })
                    }
                    onToneChange={(tone) => onChange({ ...module, infoCardGrid: { ...module.infoCardGrid!, tone } })}
                />
                <InspectorGroup label="Cards">
                    <InspectorField label="Eyebrow" htmlFor="infoCardEyebrow">
                        <Input
                            id="infoCardEyebrow"
                            value={module.infoCardGrid?.eyebrow ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.infoCardGridEyebrow)}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    infoCardGrid: { ...module.infoCardGrid!, eyebrow: event.target.value },
                                })
                            }
                        />
                    </InspectorField>
                    <InspectorField label="Heading" htmlFor="infoCardHeading">
                        <Input
                            id="infoCardHeading"
                            value={module.infoCardGrid?.heading ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.infoCardGridHeading)}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    infoCardGrid: { ...module.infoCardGrid!, heading: event.target.value },
                                })
                            }
                        />
                    </InspectorField>
                    <InspectorField label="Body" htmlFor="infoCardBody">
                        <Textarea
                            id="infoCardBody"
                            rows={3}
                            value={module.infoCardGrid?.body ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.infoCardGridBody)}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    infoCardGrid: { ...module.infoCardGrid!, body: event.target.value },
                                })
                            }
                        />
                    </InspectorField>
                    <CmsListEditor<CmsGridListItem>
                        label="Card"
                        addLabel="Add card"
                        emptyText="No cards yet."
                        items={module.infoCardGrid?.items ?? []}
                        fields={gridItemFields(fieldDescriptors, {
                            title: CMS_FIELD_PATHS.infoCardGridItemTitle,
                            body: CMS_FIELD_PATHS.infoCardGridItemBody,
                            ctaLabel: CMS_FIELD_PATHS.infoCardGridItemCtaLabel,
                        })}
                        disabled={!canEdit}
                        pathPrefix="infoCardGrid.items"
                        warningPaths={warningPaths}
                        createItem={createGridItem}
                        normalizeItem={normalizeGridItem}
                        onChange={(items) => onChange({ ...module, infoCardGrid: { ...module.infoCardGrid!, items } })}
                    />
                </InspectorGroup>
            </>
        );
    }

    if (module.moduleType === CmsPageModuleType.MEDIA_TEXT_SECTION) {
        return (
            <>
                <InspectorGroup label="Content">
                    {(['eyebrow', 'heading'] as const).map((field) => (
                        <InspectorField key={field} label={field} htmlFor={`media-text-${field}`}>
                            <Input
                                id={`media-text-${field}`}
                                value={module.mediaTextSection?.[field] ?? ''}
                                disabled={!canEdit}
                                maxLength={
                                    field === 'heading'
                                        ? maxLength(CMS_FIELD_PATHS.mediaTextSectionHeading)
                                        : maxLength(CMS_FIELD_PATHS.mediaTextSectionEyebrow)
                                }
                                onChange={(event) =>
                                    onChange({
                                        ...module,
                                        mediaTextSection: {
                                            ...module.mediaTextSection!,
                                            [field]: event.target.value,
                                        },
                                    })
                                }
                            />
                        </InspectorField>
                    ))}
                    <InspectorField label="Body" htmlFor="mediaTextBody">
                        <Textarea
                            id="mediaTextBody"
                            rows={5}
                            value={module.mediaTextSection?.body ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.mediaTextSectionBody)}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    mediaTextSection: { ...module.mediaTextSection!, body: event.target.value },
                                })
                            }
                        />
                    </InspectorField>
                    {(['ctaLabel', 'ctaUrl'] as const).map((field) => {
                        const value = module.mediaTextSection?.[field] ?? '';
                        const path = `mediaTextSection.${field}`;
                        const fieldIsUrl = isUrlField(field);
                        return (
                            <InspectorField key={field} label={field} htmlFor={`media-text-${field}`}>
                                <Input
                                    id={`media-text-${field}`}
                                    type={inputTypeForField(field)}
                                    value={value}
                                    disabled={!canEdit}
                                    maxLength={
                                        field === 'ctaLabel'
                                            ? maxLength(CMS_FIELD_PATHS.mediaTextSectionCtaLabel)
                                            : undefined
                                    }
                                    aria-invalid={fieldIsUrl && cmsHttpsUrlValidationMessage(value) ? true : undefined}
                                    className={fieldIsUrl ? urlFieldClass(path, value) : fieldWarning(path)}
                                    onChange={(event) =>
                                        onChange({
                                            ...module,
                                            mediaTextSection: {
                                                ...module.mediaTextSection!,
                                                [field]: event.target.value,
                                            },
                                        })
                                    }
                                    onBlur={(event) => {
                                        if (!fieldIsUrl) {
                                            return;
                                        }
                                        onChange({
                                            ...module,
                                            mediaTextSection: {
                                                ...module.mediaTextSection!,
                                                [field]: normalizeUrlInputValue(event.target.value),
                                            },
                                        });
                                    }}
                                />
                            </InspectorField>
                        );
                    })}
                </InspectorGroup>
                <InspectorGroup label="Media">
                    <CmsImageAssetInput
                        key={moduleKey}
                        cmsMicrositeGUID={cmsMicrositeGUID}
                        assetGuid={module.mediaTextSection?.imageAssetId}
                        disabled={!canEdit}
                        label="Upload image"
                        onChange={(assetGuid) =>
                            onChange({
                                ...module,
                                mediaTextSection: { ...module.mediaTextSection!, imageAssetId: assetGuid },
                            })
                        }
                    />
                </InspectorGroup>
            </>
        );
    }

    if (module.moduleType === CmsPageModuleType.PROCESS_STEPS) {
        return (
            <InspectorGroup label="Steps">
                <InspectorField label="Eyebrow" htmlFor="processEyebrow">
                    <Input
                        id="processEyebrow"
                        value={module.processSteps?.eyebrow ?? ''}
                        disabled={!canEdit}
                        maxLength={maxLength(CMS_FIELD_PATHS.processStepsEyebrow)}
                        onChange={(event) =>
                            onChange({
                                ...module,
                                processSteps: { ...module.processSteps!, eyebrow: event.target.value },
                            })
                        }
                    />
                </InspectorField>
                <InspectorField label="Heading" htmlFor="processHeading">
                    <Input
                        id="processHeading"
                        value={module.processSteps?.heading ?? ''}
                        disabled={!canEdit}
                        maxLength={maxLength(CMS_FIELD_PATHS.processStepsHeading)}
                        onChange={(event) =>
                            onChange({
                                ...module,
                                processSteps: { ...module.processSteps!, heading: event.target.value },
                            })
                        }
                    />
                </InspectorField>
                <InspectorField label="Body" htmlFor="processBody">
                    <Textarea
                        id="processBody"
                        rows={3}
                        value={module.processSteps?.body ?? ''}
                        disabled={!canEdit}
                        maxLength={maxLength(CMS_FIELD_PATHS.processStepsBody)}
                        onChange={(event) =>
                            onChange({
                                ...module,
                                processSteps: { ...module.processSteps!, body: event.target.value },
                            })
                        }
                    />
                </InspectorField>
                <CmsListEditor<CmsProcessStepListItem>
                    label="Step"
                    addLabel="Add step"
                    emptyText="No steps yet."
                    items={module.processSteps?.steps ?? []}
                    fields={processStepFields(fieldDescriptors)}
                    disabled={!canEdit}
                    warningPaths={warningPaths}
                    createItem={createProcessStep}
                    onChange={(steps) => onChange({ ...module, processSteps: { ...module.processSteps!, steps } })}
                />
            </InspectorGroup>
        );
    }

    if (module.moduleType === CmsPageModuleType.QUOTE_SECTION) {
        const quoteLayout = module.quoteSection?.layout ?? CmsQuoteSectionLayout.LEFT;
        return (
            <>
                <QuoteSectionDesignControls
                    layout={quoteLayout}
                    disabled={!canEdit}
                    onLayoutChange={(layout) =>
                        onChange({
                            ...module,
                            quoteSection: {
                                ...module.quoteSection!,
                                layout,
                                imageAssetId:
                                    layout === CmsQuoteSectionLayout.CENTERED
                                        ? undefined
                                        : module.quoteSection?.imageAssetId,
                            },
                        })
                    }
                />
                <InspectorGroup label="Quote">
                    <InspectorField label="Eyebrow" htmlFor="quoteEyebrow">
                        <Input
                            id="quoteEyebrow"
                            value={module.quoteSection?.eyebrow ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.quoteSectionEyebrow)}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    quoteSection: { ...module.quoteSection!, eyebrow: event.target.value },
                                })
                            }
                        />
                    </InspectorField>
                    <InspectorField label="Quote" htmlFor="quoteText">
                        <Textarea
                            id="quoteText"
                            rows={5}
                            value={module.quoteSection?.quote ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.quoteSectionQuote)}
                            className={fieldWarning('quoteSection.quote')}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    quoteSection: { ...module.quoteSection!, quote: event.target.value },
                                })
                            }
                        />
                    </InspectorField>
                    {(['attributionName', 'attributionTitle', 'ctaLabel', 'ctaUrl'] as const).map((field) => {
                        const value = module.quoteSection?.[field] ?? '';
                        const path = `quoteSection.${field}`;
                        const fieldIsUrl = isUrlField(field);
                        return (
                            <InspectorField key={field} label={field} htmlFor={`quote-${field}`}>
                                <Input
                                    id={`quote-${field}`}
                                    type={inputTypeForField(field)}
                                    value={value}
                                    disabled={!canEdit}
                                    maxLength={
                                        field === 'attributionName'
                                            ? maxLength(CMS_FIELD_PATHS.quoteSectionAttributionName)
                                            : field === 'attributionTitle'
                                              ? maxLength(CMS_FIELD_PATHS.quoteSectionAttributionTitle)
                                              : field === 'ctaLabel'
                                                ? maxLength(CMS_FIELD_PATHS.quoteSectionCtaLabel)
                                                : undefined
                                    }
                                    aria-invalid={fieldIsUrl && cmsHttpsUrlValidationMessage(value) ? true : undefined}
                                    className={fieldIsUrl ? urlFieldClass(path, value) : fieldWarning(path)}
                                    onChange={(event) =>
                                        onChange({
                                            ...module,
                                            quoteSection: { ...module.quoteSection!, [field]: event.target.value },
                                        })
                                    }
                                    onBlur={(event) => {
                                        if (!fieldIsUrl) {
                                            return;
                                        }
                                        onChange({
                                            ...module,
                                            quoteSection: {
                                                ...module.quoteSection!,
                                                [field]: normalizeUrlInputValue(event.target.value),
                                            },
                                        });
                                    }}
                                />
                            </InspectorField>
                        );
                    })}
                </InspectorGroup>
                {quoteLayout === CmsQuoteSectionLayout.LEFT ? (
                    <InspectorGroup label="Media">
                        <CmsImageAssetInput
                            key={moduleKey}
                            cmsMicrositeGUID={cmsMicrositeGUID}
                            assetGuid={module.quoteSection?.imageAssetId}
                            disabled={!canEdit}
                            label="Upload image"
                            onChange={(assetGuid) =>
                                onChange({
                                    ...module,
                                    quoteSection: { ...module.quoteSection!, imageAssetId: assetGuid },
                                })
                            }
                        />
                    </InspectorGroup>
                ) : null}
            </>
        );
    }

    if (module.moduleType === CmsPageModuleType.RESOURCE_LIST) {
        return (
            <InspectorGroup label="Resources">
                <InspectorField label="Eyebrow" htmlFor="resourceEyebrow">
                    <Input
                        id="resourceEyebrow"
                        value={module.resourceList?.eyebrow ?? ''}
                        disabled={!canEdit}
                        maxLength={maxLength(CMS_FIELD_PATHS.resourceListEyebrow)}
                        onChange={(event) =>
                            onChange({
                                ...module,
                                resourceList: { ...module.resourceList!, eyebrow: event.target.value },
                            })
                        }
                    />
                </InspectorField>
                <InspectorField label="Heading" htmlFor="resourceHeading">
                    <Input
                        id="resourceHeading"
                        value={module.resourceList?.heading ?? ''}
                        disabled={!canEdit}
                        maxLength={maxLength(CMS_FIELD_PATHS.resourceListHeading)}
                        onChange={(event) =>
                            onChange({
                                ...module,
                                resourceList: { ...module.resourceList!, heading: event.target.value },
                            })
                        }
                    />
                </InspectorField>
                <InspectorField label="Body" htmlFor="resourceBody">
                    <Textarea
                        id="resourceBody"
                        rows={3}
                        value={module.resourceList?.body ?? ''}
                        disabled={!canEdit}
                        maxLength={maxLength(CMS_FIELD_PATHS.resourceListBody)}
                        onChange={(event) =>
                            onChange({
                                ...module,
                                resourceList: { ...module.resourceList!, body: event.target.value },
                            })
                        }
                    />
                </InspectorField>
                <CmsListEditor<CmsResourceListEditorItem>
                    label="Resource"
                    addLabel="Add resource"
                    emptyText="No resources yet."
                    items={module.resourceList?.items ?? []}
                    fields={resourceItemFields(fieldDescriptors)}
                    disabled={!canEdit}
                    pathPrefix="resourceList.items"
                    warningPaths={warningPaths}
                    createItem={createResourceItem}
                    onChange={(items) => onChange({ ...module, resourceList: { ...module.resourceList!, items } })}
                />
            </InspectorGroup>
        );
    }

    if (module.moduleType === CmsPageModuleType.IMPORTANT_DISCLOSURES) {
        return (
            <>
                <InspectorGroup label="Disclosures">
                    <InspectorField label="Heading" htmlFor="importantDisclosuresHeading">
                        <Input
                            id="importantDisclosuresHeading"
                            value={module.importantDisclosures?.heading ?? ''}
                            disabled={!canEdit}
                            maxLength={maxLength(CMS_FIELD_PATHS.importantDisclosuresHeading)}
                            className={fieldWarning('importantDisclosures.heading')}
                            onChange={(event) =>
                                onChange({
                                    ...module,
                                    importantDisclosures: {
                                        ...module.importantDisclosures!,
                                        heading: event.target.value,
                                    },
                                })
                            }
                        />
                    </InspectorField>
                    <CmsListEditor<CmsImportantDisclosureParagraphListItem>
                        label="Paragraph"
                        addLabel="Add paragraph"
                        emptyText="No disclosure paragraphs yet."
                        items={module.importantDisclosures?.paragraphs ?? []}
                        fields={importantDisclosureParagraphFields(fieldDescriptors)}
                        disabled={!canEdit}
                        pathPrefix="importantDisclosures.paragraphs"
                        warningPaths={warningPaths}
                        createItem={createImportantDisclosureParagraph}
                        onChange={(paragraphs) =>
                            onChange({
                                ...module,
                                importantDisclosures: { ...module.importantDisclosures!, paragraphs },
                            })
                        }
                    />
                </InspectorGroup>
                <InspectorGroup label="Footnotes">
                    <CmsListEditor<CmsImportantDisclosureFootnoteListItem>
                        label="Footnote"
                        addLabel="Add footnote"
                        emptyText="No footnotes yet."
                        items={module.importantDisclosures?.footnotes ?? []}
                        fields={importantDisclosureFootnoteFields(fieldDescriptors)}
                        disabled={!canEdit}
                        pathPrefix="importantDisclosures.footnotes"
                        warningPaths={warningPaths}
                        createItem={createImportantDisclosureFootnote}
                        normalizeItem={normalizeImportantDisclosureFootnote}
                        renderItemAccessory={(item) => <DisclosureFootnoteTokenAccessory item={item} />}
                        onChange={(footnotes) =>
                            onChange({
                                ...module,
                                importantDisclosures: { ...module.importantDisclosures!, footnotes },
                            })
                        }
                    />
                </InspectorGroup>
            </>
        );
    }

    if (module.moduleType === CmsPageModuleType.FAQ) {
        return (
            <InspectorGroup label="FAQ">
                <InspectorField label="Eyebrow" htmlFor="faqEyebrow">
                    <Input
                        id="faqEyebrow"
                        value={module.faq?.eyebrow ?? ''}
                        disabled={!canEdit}
                        maxLength={maxLength(CMS_FIELD_PATHS.faqEyebrow)}
                        onChange={(event) =>
                            onChange({ ...module, faq: { ...module.faq!, eyebrow: event.target.value } })
                        }
                    />
                </InspectorField>
                <InspectorField label="Heading" htmlFor="faqHeading">
                    <Input
                        id="faqHeading"
                        value={module.faq?.heading ?? ''}
                        disabled={!canEdit}
                        maxLength={maxLength(CMS_FIELD_PATHS.faqHeading)}
                        onChange={(event) =>
                            onChange({ ...module, faq: { ...module.faq!, heading: event.target.value } })
                        }
                    />
                </InspectorField>
                <CmsListEditor<CmsFaqListItem>
                    label="FAQ"
                    addLabel="Add FAQ"
                    emptyText="No FAQ items yet."
                    items={module.faq?.items ?? []}
                    fields={faqItemFields(fieldDescriptors)}
                    disabled={!canEdit}
                    pathPrefix="faq.items"
                    warningPaths={warningPaths}
                    createItem={createFaqItem}
                    onChange={(items) => onChange({ ...module, faq: { ...module.faq!, items } })}
                />
            </InspectorGroup>
        );
    }

    return null;
};

export const CmsVisualEditor = () => {
    const { cmsMicrositeGUID = '' } = useParams();
    const permissions = useCmsPermissions();
    const { close: closeAiChatSidebar, selectedContent } = useAiChatSidebarState();
    const micrositeQuery = useCmsMicrosite(cmsMicrositeGUID);
    const draftQuery = useCmsHomepageContent(cmsMicrositeGUID);
    const moduleDescriptorsQuery = useCmsHomepageModuleDescriptors();
    const historyQuery = useCmsHistory(cmsMicrositeGUID);
    const saveMutation = useUpdateCmsHomepageContent(cmsMicrositeGUID);
    const submitMutation = useSubmitCmsForReview(cmsMicrositeGUID);
    const improveMutation = useImproveCmsHomepageModuleContent(cmsMicrositeGUID);
    const [draft, setDraft] = useState<CmsDraftPageContent>();
    const [savedDraft, setSavedDraft] = useState<CmsDraftPageContent>();
    const [originalUpdatedAt, setOriginalUpdatedAt] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [improvementPointers, setImprovementPointers] = useState('');
    const [improvementProposal, setImprovementProposal] = useState<CmsImproveContentResponse>();
    const [improveDialogOpen, setImproveDialogOpen] = useState(false);
    const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [reviewNotes, setReviewNotes] = useState('');

    useEffect(() => {
        if (selectedContent === 'sidekick') {
            closeAiChatSidebar();
        }
    }, [closeAiChatSidebar, selectedContent]);

    useEffect(() => {
        if (draftQuery.data) {
            setDraft(draftQuery.data);
            setSavedDraft(draftQuery.data);
            setOriginalUpdatedAt(draftQuery.data.updatedAt);
        }
    }, [draftQuery.data]);

    const latestWorkflowStatus = historyQuery.data?.versions[0]?.latestWorkflowStatus;
    const canEditMicrosite = permissions.canEditMicrosite(micrositeQuery.data?.ownerUserGUID);
    const preparedDraft = useMemo(() => (draft ? draftContentForSave(draft) : undefined), [draft]);
    const dirty = useMemo(
        () =>
            !!draft &&
            !!savedDraft &&
            (JSON.stringify(draft) !== JSON.stringify(savedDraft) ||
                JSON.stringify(preparedDraft) !== JSON.stringify(savedDraft)),
        [draft, preparedDraft, savedDraft],
    );
    const blocker = useBlocker(useCallback(() => dirty, [dirty]));

    useBeforeUnload(
        useCallback(
            (event: BeforeUnloadEvent) => {
                if (!dirty) {
                    return;
                }
                event.preventDefault();
                event.returnValue = '';
            },
            [dirty],
        ),
    );
    const fieldDescriptors = useMemo(
        () => buildCmsFieldDescriptorLookup(moduleDescriptorsQuery.data),
        [moduleDescriptorsQuery.data],
    );
    const submitValidation = useMemo(
        () => (preparedDraft ? validateCmsDraftForSubmit(preparedDraft) : { issues: [], isValid: true }),
        [preparedDraft],
    );
    const submitIssuesByModuleIndex = useMemo(
        () => cmsValidationIssuesByModuleIndex(submitValidation.issues),
        [submitValidation.issues],
    );

    const modules = draft?.modules ?? [];
    const safeIndex = Math.min(selectedIndex, Math.max(modules.length - 1, 0));
    const selectedModule = modules[safeIndex] as CmsPageModuleContent | undefined;
    const addableTypes = getAddableCmsModuleTypes(modules);
    const selectedTitle = selectedModule ? getCmsModuleDisplayTitle(modules, safeIndex) : 'No section';

    useEffect(() => {
        setImprovementProposal(undefined);
    }, [safeIndex, selectedModule?.moduleType, selectedModule?.cmsPageModuleGUID]);

    const updateModuleAt = (index: number, next: CmsPageModuleContent) => {
        setDraft((current) =>
            current
                ? { ...current, modules: current.modules.map((module, i) => (i === index ? next : module)) }
                : current,
        );
    };

    const moveModule = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= modules.length) {
            return;
        }
        setDraft((current) => {
            if (!current) {
                return current;
            }
            const next = [...current.modules];
            [next[index], next[target]] = [next[target], next[index]];
            return { ...current, modules: next };
        });
        setSelectedIndex(target);
    };

    const removeModule = (index: number) => {
        setDraft((current) =>
            current ? { ...current, modules: current.modules.filter((_, i) => i !== index) } : current,
        );
        setSelectedIndex((current) => Math.max(0, current > index ? current - 1 : current));
    };

    const addModule = (moduleType: CmsPageModuleType) => {
        setDraft((current) => {
            if (!current) {
                return current;
            }
            const next = [...current.modules, defaultModule(moduleType)];
            setSelectedIndex(next.length - 1);
            return { ...current, modules: next };
        });
    };

    const improveSelectedModule = async () => {
        if (!draft || !selectedModule) {
            return;
        }
        const response = await improveMutation.mutateAsync({
            draftPageContent: draft,
            draftModule: selectedModule,
            moduleIndex: safeIndex,
            pointers: improvementPointers,
        });
        setImprovementProposal(response);
    };

    const applyImprovementProposal = () => {
        if (!improvementProposal?.proposedModule) {
            return;
        }
        updateModuleAt(safeIndex, improvementProposal.proposedModule);
        setImprovementProposal(undefined);
        setImproveDialogOpen(false);
    };

    const focusFirstValidationIssue = (issues: typeof submitValidation.issues) => {
        const firstModuleIssue = issues.find((issue) => issue.moduleIndex != null);
        if (firstModuleIssue?.moduleIndex != null) {
            setSelectedIndex(firstModuleIssue.moduleIndex);
        }
    };

    const save = async () => {
        if (!draft) {
            return undefined;
        }
        const content = draftContentForSave(draft);
        const validation = validateCmsDraftForSave(content);
        if (!validation.isValid) {
            focusFirstValidationIssue(validation.issues);
            Toast.error('This draft has a section that needs to be re-added before saving.', 'info');
            return undefined;
        }
        const saved = await saveMutation.mutateAsync({
            content,
            expectedUpdatedAt: originalUpdatedAt,
        });
        if (saved) {
            setDraft(saved);
            setSavedDraft(saved);
            setOriginalUpdatedAt(saved.updatedAt);
        }
        return saved;
    };

    const submitForReview = async () => {
        const persisted = dirty ? await save() : draft;
        if (!persisted) {
            return;
        }
        await submitMutation.mutateAsync({ expectedDraftUpdatedAt: persisted.updatedAt, comment: reviewNotes });
        setSubmitDialogOpen(false);
        setReviewNotes('');
    };

    if (micrositeQuery.isError || draftQuery.isError || moduleDescriptorsQuery.isError) {
        return (
            <div className="flex h-[calc(100vh-120px)] items-center justify-center">
                <p className="text-destructive text-sm">Unable to load the visual editor.</p>
            </div>
        );
    }

    if (micrositeQuery.isLoading || draftQuery.isLoading || moduleDescriptorsQuery.isLoading) {
        return (
            <div className="flex h-[calc(100vh-120px)] items-center justify-center">
                <p className="text-muted-foreground text-sm">Loading visual editor…</p>
            </div>
        );
    }

    if (!micrositeQuery.data || !draft) {
        return (
            <div className="flex h-[calc(100vh-120px)] items-center justify-center">
                <p className="text-destructive text-sm">This CMS draft could not be loaded.</p>
            </div>
        );
    }

    const microsite = micrositeQuery.data;
    const selectedMeta = selectedModule ? MODULE_META[selectedModule.moduleType] : undefined;

    return (
        <div className="flex h-[calc(100vh-120px)] flex-col bg-white">
            {/* Toolbar */}
            <header className="flex items-center justify-between gap-4 border-b bg-white px-5 py-3">
                <div className="flex min-w-0 items-center gap-3">
                    <Link
                        className="text-muted-foreground hover:text-foreground focus:ring-ring inline-flex shrink-0 items-center gap-1.5 rounded-sm text-sm font-medium transition focus:ring-2 focus:outline-none"
                        to={marketingCmsSiteHistoryPath(cmsMicrositeGUID)}
                    >
                        <ChevronLeftIcon className="size-4" />
                        Version history
                    </Link>
                    <LuChevronRight className="text-muted-foreground size-4 shrink-0" />
                    <div className="min-w-0">
                        <h1 className="truncate text-base font-semibold">{microsite.siteName}</h1>
                        <p
                            className={cn(
                                'flex items-center gap-1.5 text-xs',
                                dirty ? 'text-muted-foreground' : 'text-emerald-600',
                            )}
                        >
                            {dirty ? (
                                <>
                                    <Pencil1Icon className="h-3 w-3" /> Unsaved changes
                                </>
                            ) : (
                                <>
                                    <CheckCircledIcon className="h-3 w-3" /> All changes saved
                                </>
                            )}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <CmsStatusBadge status={latestWorkflowStatus} />
                    <Button asChild variant="ghost" size="sm">
                        <Link to={marketingCmsSiteHistoryPath(cmsMicrositeGUID)}>History</Link>
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!canEditMicrosite || !dirty || saveMutation.isPending}
                        onClick={save}
                    >
                        <FileTextIcon className="mr-1.5 h-4 w-4" /> Save draft
                    </Button>
                    <Button
                        size="sm"
                        disabled={!canEditMicrosite || submitMutation.isPending}
                        onClick={() => setSubmitDialogOpen(true)}
                    >
                        <RocketIcon className="mr-1.5 h-4 w-4" /> Submit for review
                    </Button>
                </div>
            </header>

            <Dialog
                open={submitDialogOpen}
                onOpenChange={(open) => {
                    if (!submitMutation.isPending && !saveMutation.isPending) {
                        setSubmitDialogOpen(open);
                    }
                }}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Submit for review</DialogTitle>
                        <DialogDescription>
                            Add optional notes for the page content reviewer before submitting this draft.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="cmsReviewNotes">Reviewer notes</Label>
                        <Textarea
                            id="cmsReviewNotes"
                            rows={5}
                            value={reviewNotes}
                            disabled={submitMutation.isPending || saveMutation.isPending}
                            placeholder="Optional context, questions, or areas to review."
                            onChange={(event) => setReviewNotes(event.target.value)}
                        />
                    </div>
                    {submitValidation.issues.length > 0 && (
                        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                            <p className="font-medium">Recommended fields to fill</p>
                            <ul className="list-disc space-y-1 pl-4">
                                {submitValidation.issues.slice(0, 5).map((issue, index) => (
                                    <li key={`${issue.path ?? 'page'}-${index}`}>{issue.message}</li>
                                ))}
                            </ul>
                            {submitValidation.issues.length > 5 && (
                                <p className="text-xs">And {submitValidation.issues.length - 5} more.</p>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            disabled={submitMutation.isPending || saveMutation.isPending}
                            onClick={() => setSubmitDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            disabled={submitMutation.isPending || saveMutation.isPending}
                            onClick={submitForReview}
                        >
                            Submit for review
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={improveDialogOpen}
                onOpenChange={(open) => {
                    if (!improveMutation.isPending) {
                        setImproveDialogOpen(open);
                    }
                }}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Improve content with Claire™</DialogTitle>
                        <DialogDescription>
                            Add optional guidance for Claire before improving the selected section.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="improvementPointers">Pointers for AI</Label>
                            <Textarea
                                id="improvementPointers"
                                rows={4}
                                value={improvementPointers}
                                disabled={!canEditMicrosite || improveMutation.isPending}
                                maxLength={2000}
                                onChange={(event) => setImprovementPointers(event.target.value)}
                            />
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={!canEditMicrosite || improveMutation.isPending || !selectedModule}
                            onClick={improveSelectedModule}
                        >
                            {improveMutation.isPending ? 'Improving…' : 'Improve content'}
                        </Button>
                        {improvementProposal && (
                            <div className="space-y-3 rounded-md border bg-stone-50 p-3">
                                <div className="space-y-1">
                                    <p className="text-sm font-medium">Proposal ready</p>
                                    {improvementProposal.changedFields.length > 0 ? (
                                        <p className="text-muted-foreground text-xs">
                                            Changed {improvementProposal.changedFields.join(', ')}
                                        </p>
                                    ) : (
                                        <p className="text-muted-foreground text-xs">No text changes found.</p>
                                    )}
                                </div>
                                {improvementProposal.warnings.length > 0 && (
                                    <ul className="text-muted-foreground list-disc space-y-1 pl-4 text-xs">
                                        {improvementProposal.warnings.map((warning, index) => (
                                            <li key={`${warning}-${index}`}>{warning}</li>
                                        ))}
                                    </ul>
                                )}
                                <div className="flex items-center gap-2">
                                    <Button
                                        type="button"
                                        size="sm"
                                        disabled={!canEditMicrosite}
                                        onClick={applyImprovementProposal}
                                    >
                                        Apply
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setImprovementProposal(undefined)}
                                    >
                                        Discard
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <CmsSectionPicker open={pickerOpen} modules={modules} onAdd={addModule} onOpenChange={setPickerOpen} />

            {/* Body: layers | canvas | inspector */}
            <div className="grid min-h-0 flex-1 grid-cols-[260px_1fr_340px]">
                {/* Left: layers + add */}
                <aside className="flex min-h-0 flex-col border-r bg-white">
                    <div className="flex items-center justify-between px-4 pt-4 pb-2">
                        <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">Layers</p>
                        <Button
                            variant="outline"
                            size="xs"
                            disabled={!canEditMicrosite || addableTypes.length === 0}
                            onClick={() => setPickerOpen(true)}
                        >
                            <PlusIcon className="mr-1 h-3.5 w-3.5" /> Add section
                        </Button>
                    </div>
                    <ScrollArea className="flex-1">
                        <ul className="space-y-1.5 px-3 pb-4">
                            {modules.map((module, index) => {
                                const meta = MODULE_META[module.moduleType]!;
                                const Icon = meta.icon;
                                const isActive = index === safeIndex;
                                const title = getCmsModuleDisplayTitle(modules, index);
                                const issueCount = submitIssuesByModuleIndex[index]?.length ?? 0;
                                return (
                                    <li key={getCmsModuleInstanceKey(module, index)}>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedIndex(index)}
                                            className={cn(
                                                'flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
                                                isActive
                                                    ? 'border-foreground bg-stone-50'
                                                    : 'border-transparent hover:bg-stone-50',
                                            )}
                                        >
                                            <span className="bg-seafoam-100 text-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
                                                <Icon className="h-4 w-4" />
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block text-sm font-medium">{title}</span>
                                                <span className="text-muted-foreground block truncate text-xs">
                                                    {meta.subtitle}
                                                </span>
                                            </span>
                                            {issueCount > 0 && (
                                                <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 px-1 text-xs font-medium text-amber-900">
                                                    {issueCount}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                            {modules.length === 0 && (
                                <li className="text-muted-foreground px-3 py-6 text-center text-xs">
                                    No sections yet. Use “Add” to place your first block.
                                </li>
                            )}
                        </ul>
                    </ScrollArea>
                </aside>

                {/* Center: live preview canvas */}
                <main className="min-h-0 overflow-auto bg-stone-100">
                    <div className="mx-auto max-w-[1120px] shadow-sm">
                        <CmsModuleListPreview
                            draft={draft}
                            selectedIndex={safeIndex}
                            onSelectIndex={setSelectedIndex}
                        />
                    </div>
                </main>

                {/* Right: inspector */}
                <aside className="flex min-h-0 flex-col border-l bg-white">
                    <div className="flex items-center justify-between gap-2 border-b px-5 py-3">
                        <div className="flex min-w-0 items-center gap-2">
                            {selectedMeta && <selectedMeta.icon className="h-4 w-4 shrink-0" />}
                            <h2 className="truncate text-sm font-semibold">{selectedTitle}</h2>
                        </div>
                        {selectedModule && (
                            <div className="flex items-center gap-1">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Improve section with Claire™"
                                    disabled={!canEditMicrosite}
                                    onClick={() => setImproveDialogOpen(true)}
                                >
                                    <LuSparkles className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Move section up"
                                    disabled={!canEditMicrosite || safeIndex === 0}
                                    onClick={() => moveModule(safeIndex, -1)}
                                >
                                    <ChevronUpIcon className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Move section down"
                                    disabled={!canEditMicrosite || safeIndex === modules.length - 1}
                                    onClick={() => moveModule(safeIndex, 1)}
                                >
                                    <ChevronDownIcon className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Remove section"
                                    disabled={!canEditMicrosite || !canRemoveCmsModule(modules, safeIndex)}
                                    onClick={() => removeModule(safeIndex)}
                                >
                                    <TrashIcon className="h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                    <ScrollArea className="flex-1">
                        <div className="space-y-6 px-5 py-5">
                            <SectionInspector
                                cmsMicrositeGUID={cmsMicrositeGUID}
                                moduleKey={selectedModule ? getCmsModuleInstanceKey(selectedModule, safeIndex) : 'none'}
                                module={selectedModule}
                                modules={modules}
                                canEdit={canEditMicrosite}
                                fieldDescriptors={fieldDescriptors}
                                issues={submitIssuesByModuleIndex[safeIndex] ?? []}
                                onChange={(next) => updateModuleAt(safeIndex, next)}
                            />
                        </div>
                    </ScrollArea>
                </aside>
            </div>
            <ConfirmModal
                open={blocker.state === 'blocked'}
                onOpenChange={(open) => {
                    if (!open) blocker.reset?.();
                }}
                title="You have unsaved changes"
                description="Are you sure you want to leave? Your unsaved changes will be lost."
                confirmText="Leave"
                cancelText="Stay"
                confirmVariant="destructive"
                onConfirm={() => blocker.proceed?.()}
                onCancel={() => blocker.reset?.()}
            />
        </div>
    );
};
